import { useIntl } from '@leagueplatform/locales';
import {
  ParagraphText,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { PreferenceToggle } from 'components/preference-toggle/preference-toggle.component';
import { SectionHeading } from 'components/section-heading/section-heading.component';
import {
  type ToggleGroupState,
  type ToggleItemConfig,
} from './communication-preferences.types';

// Title (+ optional caption) shown to the left of a toggle. Rendered inside the
// Switch's <label>, so these are spans (display:block to stack) — a block
// element here would be invalid inside the label text wrapper. Typography is
// Communication-Preferences-specific per Figma ($subtitleOne title, Text-Primary
// caption); kept local rather than in the shared PreferenceToggle so each
// screen owns its own type tokens.
const ToggleLabel = ({
  label,
  caption,
}: {
  label: string;
  caption?: string;
}) => (
  <>
    <UtilityText
      css={{
        display: 'block',
        typography: '$subtitleOne',
        color: '$onSurfaceTextPrimary',
      }}
    >
      {label}
    </UtilityText>
    {caption && (
      <UtilityText
        css={{
          display: 'block',
          typography: '$caption',
          color: '$onSurfaceTextPrimary',
        }}
      >
        {caption}
      </UtilityText>
    )}
  </>
);

// A heading + optional description, a "master" toggle, and its child toggles.
export const ToggleGroup = ({
  heading,
  description,
  masterId,
  masterLabel,
  masterCaption,
  items,
  group,
  onToggleMaster,
  onToggleItem,
}: {
  heading: string;
  description?: string;
  masterId: string;
  masterLabel: string;
  masterCaption: string;
  items: ToggleItemConfig[];
  group: ToggleGroupState;
  onToggleMaster: () => void;
  onToggleItem: (itemId: string) => void;
}) => {
  const { formatMessage } = useIntl();
  const headingId = `${masterId}-heading`;

  return (
    <StackLayout
      spacing="$oneAndHalf"
      horizontalAlignment="stretch"
      role="group"
      aria-labelledby={headingId}
    >
      <StackLayout spacing="$half" horizontalAlignment="stretch">
        <SectionHeading id={headingId}>{heading}</SectionHeading>
        {description && (
          <ParagraphText css={{ typography: '$bodyOne' }}>
            {description}
          </ParagraphText>
        )}
      </StackLayout>
      <StackLayout spacing="$one" horizontalAlignment="stretch">
        <PreferenceToggle
          id={masterId}
          label={<ToggleLabel label={masterLabel} caption={masterCaption} />}
          checked={group.master}
          onToggle={onToggleMaster}
          alignTop
        />
        {items.map((item) => (
          <PreferenceToggle
            key={item.id}
            id={`${masterId}-${item.id}`}
            label={<ToggleLabel label={formatMessage({ id: item.labelId })} />}
            checked={group.items[item.id]}
            onToggle={() => onToggleItem(item.id)}
          />
        ))}
      </StackLayout>
    </StackLayout>
  );
};
